// SpeedDatingEventsPage.js
import React, { useEffect, useState, useCallback } from "react";
import apiClient from "../api/client";
import { fetchUpcomingSpeedDatingEvents, joinSpeedDatingEvent } from "../api/speeddating";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { useEntitlementsSafe } from '../context/EntitlementsContext';
import EligibilityGateModal from "../components/EligibilityGateModal";
import { normalizeList } from "../utils/normalize";
import InlineNotice from "../components/common/InlineNotice";


function EventCard({ event, onJoin, joining }) {
  const navigate = useNavigate();
  const start = event.starts_at ? new Date(event.starts_at) : null;

  const dateLabel = start
    ? start.toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : "TBA";

  const totalMinutes = (event.round_duration_seconds * event.total_rounds) / 60;

  const handleClick = () => {
    navigate(`/events/speed-dating/${event.id}`);
  };

  return (
    <div
      className="rounded-3xl bg-gradient-to-br from-pink-600/40 via-purple-700/40 to-black/60 border border-white/10 p-4 sm:p-5 shadow-xl hover:border-pink-400/70 cursor-pointer transition-all"
      onClick={handleClick}
    >
      <div className="flex justify-between items-start gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
            ⚡ {event.title || "Speed Dating Event"}
            {event.is_demo && (
              <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-400/30 text-yellow-300 text-xs font-medium">
                Demo
              </span>
            )}
          </h2>
          <p className="text-xs sm:text-sm text-white/70 mt-1">{dateLabel}</p>
          <p className="text-xs sm:text-sm text-white/60 mt-1">
            {event.total_rounds} rounds · {totalMinutes} min ·{" "}
            {event.entry_fee_coins
              ? `${event.entry_fee_coins} LoveCoins entry`
              : "Free entry"}
          </p>
          {event.participant_count != null && (
            <p className="text-xs text-white/60 mt-1">
              {event.participant_count} joined
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center gap-3">
        <span className="text-xs sm:text-sm text-white/70">
          Tap card for event lobby
        </span>
        <button
          type="button"
          className="px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white text-pink-600 hover:bg-pink-100 shadow-md"
          onClick={(e) => {
            e.stopPropagation();
            onJoin();
          }}
          disabled={joining}
        >
          {joining ? "Joining…" : "Join"}
        </button>
      </div>
    </div>
  );
}

export default function SpeedDatingEventsPage() {
  const { user, token } = useAuth();
  const { hasFeature, isVerified } = useEntitlementsSafe();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  
  // Feature checks from entitlements
  const userIsVerified = isVerified('identity_verified');
  const userIsAgeVerified = isVerified('age_verified');
  const hasInnerCircle = hasFeature('has_inner_circle_badge');

  // Eligibility gate modal state
  const [gate, setGate] = useState({ open: false, reason: "unknown", message: "" });
  const closeGate = () => setGate({ open: false, reason: "unknown", message: "" });
  const openGate = (reason, message) => setGate({ open: true, reason: reason || "unknown", message: message || "" });

  const goGate = (href) => {
    closeGate();
    navigate(href);
  };

  // Check eligibility before joining
  const fetchEligibility = useCallback(async (eventId) => {
    try {
      const res = await apiClient.get(`/video/speed-dating/eligibility`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: eventId ? { event_id: eventId } : {},
      });
      return res.data;
    } catch (err) {
      // If eligibility check fails, return a structured error
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const reason = err?.response?.data?.reason;
      
      if (status === 401) {
        return { eligible: false, reason: "not_authenticated" };
      }
      if (status === 403) {
        return { eligible: false, reason: reason || "verification_required", message: detail };
      }
      return { eligible: false, reason: "unknown", message: detail || "Could not check eligibility" };
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchUpcomingSpeedDatingEvents();
        setEvents(normalizeList(data, ['events']));
      } catch (err) {
        console.error(err);
        setError("Unable to load events right now.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleJoin = async (eventId) => {
    // 0) Must be logged in
    if (!user || !token) {
      openGate("not_authenticated");
      return;
    }

    // 1) Check age verification before joining (use entitlements, not user object)
    if (!userIsAgeVerified) {
      openGate("age_verification_required");
      return;
    }

    setError("");
    setJoiningId(eventId);

    try {
      // 2) Gate check: Verify eligibility BEFORE attempting to join
      const elig = await fetchEligibility(eventId);
      if (!elig?.eligible) {
        openGate(elig?.reason || "unknown", elig?.message || "");
        setJoiningId(null);
        return;
      }

      // 3) Proceed with join if eligible
      await joinSpeedDatingEvent(eventId);
      // After joining, navigate to the event lobby
      navigate(`/events/speed-dating/${eventId}`);
      
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      const errorMsg = err?.response?.data?.detail || "";
      const reason = err?.response?.data?.reason;

      // 4) Handle specific error cases with the gate modal
      if (status === 401) {
        openGate("not_authenticated");
        return;
      }
      
      if (status === 403) {
        // Map known error messages to gate reasons
        if (errorMsg.includes('AGE_VERIFICATION_REQUIRED')) {
          openGate("age_verification_required");
        } else if (errorMsg.includes('IDENTITY') || errorMsg.includes('verification')) {
          openGate("verification_required", errorMsg);
        } else if (errorMsg.includes('ELITE') || errorMsg.includes('elite')) {
          openGate("elite_required", errorMsg);
        } else if (errorMsg.includes('INNER_CIRCLE') || errorMsg.includes('inner circle')) {
          openGate("inner_circle_required", errorMsg);
        } else if (errorMsg.includes('COIN') || errorMsg.includes('coin')) {
          openGate("insufficient_coins", errorMsg);
        } else if (errorMsg.includes('minutes') || errorMsg.includes('budget') || reason === 'video_budget_exceeded') {
          openGate("video_budget_exceeded", errorMsg);
        } else {
          // Use backend-provided reason if available, otherwise show generic
          openGate(reason || "unknown", errorMsg || "You can't join this event yet.");
        }
        return;
      }

      // 5) For other errors, show in the gate modal instead of inline error
      openGate("unknown", errorMsg || "Something went wrong while trying to join. Please try again.");
    } finally {
      setJoiningId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black text-white px-4 py-6 sm:px-6 sm:py-8">
      {/* Eligibility Gate Modal */}
      <EligibilityGateModal
        open={gate.open}
        reason={gate.reason}
        message={gate.message}
        onClose={closeGate}
        onAction={goGate}
      />

      <div className="max-w-4xl mx-auto">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            ⚡ Speed Dating Events
          </h1>
          <p className="text-sm sm:text-base text-white/70 mt-1">
            Join a live event, meet multiple matches in one session, and build
            real chemistry – fast.
          </p>
        </header>

        {loading && <p className="text-white/70">Loading events…</p>}
        {error && (
          <div className="mb-4">
            <InlineNotice
              variant="error"
              title="Events didn't load"
              actionLabel="Refresh"
              onAction={() => window.location.reload()}
              testId="sd-events-error"
            >
              {error}
            </InlineNotice>
          </div>
        )}

        {!loading && events.length === 0 && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="flex items-start gap-3">
              <div className="text-2xl">📹</div>

              <div className="flex-1">
                <div className="text-white text-lg font-semibold">Live Video Speed Dating</div>
                <div className="mt-1 text-white/70 text-sm">
                  Timed rounds. Real faces. Real voices. No feeds.
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                    <div className="text-white/90 text-xs font-semibold">Privacy first</div>
                    <div className="mt-1 text-white/60 text-xs">
                      No recording. No transcription. No captions. Ever.
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-3">
                    <div className="text-amber-200 text-xs font-semibold">Verified members</div>
                    <div className="mt-1 text-amber-100/70 text-xs">
                      Video unlocks after identity verification.
                    </div>
                  </div>

                  <div className="rounded-xl border border-rose-400/20 bg-rose-500/10 p-3">
                    <div className="text-rose-200 text-xs font-semibold">Special nights</div>
                    <div className="mt-1 text-rose-100/70 text-xs">
                      Ticketed themed events + Inner Circle exclusives.
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-white/70 text-sm">
                  <span className="text-white font-medium">No upcoming events yet</span> — this page is where they&apos;ll drop.
                  When events go live, you&apos;ll see dates, themes, and eligibility here.
                </div>
                
                <div className="mt-2">
                  <a href="/communities" className="text-pink-300 hover:text-pink-200 text-sm underline">
                    See all communities &amp; regions UnityLove welcomes →
                  </a>
                </div>

                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  {!user ? (
                    <button
                      type="button"
                      className="rounded-xl bg-white text-black px-4 py-2.5 text-sm font-semibold hover:bg-white/90 transition"
                      onClick={() => navigate("/auth")}
                    >
                      Sign in to join events
                    </button>
                  ) : !userIsVerified ? (
                    <button
                      type="button"
                      className="rounded-xl bg-amber-500 text-black px-4 py-2.5 text-sm font-semibold hover:bg-amber-400 transition"
                      onClick={() =>
                        navigate(`/verify-age?return=${encodeURIComponent(location.pathname)}`)
                      }
                    >
                      Verify to unlock video
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="rounded-xl bg-blue-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-400 transition"
                      onClick={() => window.location.reload()}
                    >
                      Refresh events
                    </button>
                  )}

                  <button
                    type="button"
                    className="rounded-xl border border-white/15 bg-transparent text-white px-4 py-2.5 text-sm font-semibold hover:bg-white/5 transition"
                    onClick={() => navigate("/events/special")}
                  >
                    View Special Events
                  </button>

                  {userIsVerified && !hasInnerCircle && (
                    <button
                      type="button"
                      className="rounded-xl bg-rose-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-rose-400 transition"
                      onClick={() => navigate("/shop?plan=inner-circle")}
                    >
                      Join Inner Circle
                    </button>
                  )}
                </div>

                <div className="mt-3 text-xs text-white/50">
                  This isn&apos;t Quick Match. These are scheduled rounds — when they&apos;re live, you&apos;ll feel it.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:gap-5">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              joining={joiningId === event.id}
              onJoin={() => handleJoin(event.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}